import { ArrowLeft, ExternalLink, FileText, Pin, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { get, toQuery } from '../api';
import { useAuth } from '../auth';
import { Empty, ErrorBox, LiveDot, Loading, StatusBadge } from '../components/base';
import { ReadinessPanel } from '../components/Readiness';
import { FlowChart, SeriesChart } from '../components/charts';
import {
  CATEGORY_LABELS, STATUS_LABELS, formatDate, formatNumber, metricLabel,
} from '../formats';
import { moduleLabel } from '../modules';
import type {
  Client, DocumentSummary, LastCheck, MetricAvailability, MetricPoint, Product,
} from '../types';

const RANGES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
];

const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

interface SourceStatus {
  source: string;
  label: string;
  state: 'active' | 'receiving' | 'waiting' | 'available' | 'superseded';
  detail: string;
}

const SOURCE_STATE_STYLES: Record<string, { chip: string; label: string }> = {
  active: { chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300', label: 'Active' },
  receiving: { chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300', label: 'Receiving data' },
  waiting: { chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300', label: 'Waiting' },
  available: { chip: 'bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300', label: 'Could be activated' },
  superseded: { chip: 'bg-paper-200 text-ink-500 dark:bg-ink-700 dark:text-ink-400', label: 'Superseded' },
};

/** Owner-only: which pipes feed this product, and which are dry. */
function DataSources({ sources }: { sources: SourceStatus[] }) {
  return (
    <section className="card border-dashed p-5">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
        Data sources
      </h2>
      <p className="mb-4 text-xs text-ink-500 dark:text-ink-400">
        Only you see this panel - clients see the numbers, never the plumbing.
      </p>
      <ul className="space-y-3">
        {sources.map((s) => {
          const style = SOURCE_STATE_STYLES[s.state] ?? SOURCE_STATE_STYLES.available;
          return (
            <li key={s.source} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className={`badge ${style.chip}`}>{style.label}</span>
              <span className="text-sm font-medium">{s.label}</span>
              <span className="basis-full text-xs text-ink-500 dark:text-ink-400 sm:basis-auto sm:flex-1">
                {s.detail}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface AudienceData {
  from: string;
  to: string;
  dimensions: Record<string, { key: string; total: number }[]>;
  sources: string[];
  flow: { from_path: string; to_path: string; total: number }[];
}

const AUDIENCE_LABELS: Record<string, string> = {
  path: 'Top pages',
  // Where visits START, which is a different question from which page is seen
  // most and the one that says whether a campaign landed where it aimed.
  entry_path: 'Entry pages',
  referrer: 'Sources',
  country: 'Countries',
  region: 'Regions',
  city: 'Cities',
  device: 'Devices',
  browser: 'Browsers',
  // The network the visitor came through. Not a location - an operator name -
  // and it is here because it is also what the bot filter reasons about.
  asn: 'Networks',
};

/**
 * Country keys are stored as ISO codes (that is what Cloudflare reports);
 * people read names. Intl does the mapping, so there is no list to maintain
 * and it follows the browser's language.
 */
const REGION_NAMES = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

function audienceKeyLabel(dimension: string, key: string): string {
  if (dimension !== 'country' || key.length !== 2) return key;
  try {
    return REGION_NAMES?.of(key.toUpperCase()) ?? key;
  } catch {
    return key;
  }
}

/** Ranked list with proportional bars - one card per audience dimension. */
function AudienceCard({ title, dimension, rows }: {
  title: string; dimension: string; rows: { key: string; total: number }[];
}) {
  const max = rows[0]?.total ?? 1;
  return (
    <div className="card p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
        {title}
      </h3>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.key} className="relative">
            <div
              className="absolute inset-y-0 left-0 rounded bg-brand-100 dark:bg-brand-950"
              style={{ width: `${Math.max(4, (row.total / max) * 100)}%` }}
              aria-hidden
            />
            <div className="relative flex items-center justify-between gap-3 px-2 py-1 text-sm">
              <span className="truncate">{audienceKeyLabel(dimension, row.key)}</span>
              <span className="shrink-0 font-mono text-xs tabular-nums text-ink-600 dark:text-paper-300">
                {formatNumber(row.total)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** '3 minutes ago' / '2 hours ago' - uptime checks are always recent-ish. */
function formatCheckTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${Math.round(hours / 24)} days ago`;
}

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [dataSources, setDataSources] = useState<SourceStatus[] | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [lastCheck, setLastCheck] = useState<LastCheck | null>(null);
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [available, setAvailable] = useState<MetricAvailability[]>([]);
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [metric, setMetric] = useState<string | null>(null);
  const [rangeDays, setRangeDays] = useState(90);
  const [points, setPoints] = useState<MetricPoint[] | null>(null);
  const [audience, setAudience] = useState<AudienceData | null>(null);

  useEffect(() => {
    setProduct(null);
    setError(null);
    get<{
      product: Product; client: Client; metrics_available: MetricAvailability[];
      last_check: LastCheck | null; modules: string[];
    }>(`/products/${id}`)
      .then(({ product: p, client: c, metrics_available: m, last_check: lc, modules: mods }) => {
        setProduct(p);
        setClient(c);
        setLastCheck(lc);
        setActiveModules(mods ?? []);
        setAvailable(m);
        setMetric(m.find((x) => x.metric === 'visits')?.metric ?? m[0]?.metric ?? null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
    get<{ documents: DocumentSummary[] }>(`/documents${toQuery({ product_id: id })}`)
      .then(({ documents: d }) => setDocuments(d))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!metric || !id) return;
    setPoints(null);
    get<{ points: MetricPoint[] }>(
      `/products/${id}/metrics${toQuery({ metric, from: daysAgo(rangeDays - 1) })}`,
    )
      .then(({ points: p }) => setPoints(p))
      .catch(() => setPoints([]));
  }, [id, metric, rangeDays]);

  useEffect(() => {
    if (!id) return;
    get<AudienceData>(`/products/${id}/audience${toQuery({ from: daysAgo(rangeDays - 1) })}`)
      .then(setAudience)
      .catch(() => setAudience(null));
  }, [id, rangeDays]);

  useEffect(() => {
    if (!id || user?.role !== 'owner') return;
    get<{ sources: SourceStatus[] }>(`/products/${id}/sources`)
      .then(({ sources: s }) => setDataSources(s))
      .catch(() => setDataSources(null));
  }, [id, user?.role]);

  if (error) return <ErrorBox message={error} />;
  if (!product) return <Loading />;

  const sources = [...new Set((points ?? []).map((p) => p.source).filter(Boolean))] as string[];

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/"
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-ink-500
                     hover:text-ink-700 dark:text-ink-400 dark:hover:text-paper-200"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Dashboard
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">{product.name}</h1>
          <StatusBadge status={product.status} label={STATUS_LABELS[product.status] ?? product.status} />
          <LiveDot check={lastCheck} />
        </div>
        <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
          {client?.name}
          {lastCheck && (
            <span>
              {client ? ' · ' : ''}checked {formatCheckTime(lastCheck.checked_at)}
              {lastCheck.response_ms !== null && lastCheck.ok === 1
                ? `, responded in ${lastCheck.response_ms} ms`
                : ''}
            </span>
          )}
        </p>
      </div>

      <section className="card p-5">
        <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          {product.url && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">Address</dt>
              <dd className="mt-0.5">
                <a
                  href={product.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline dark:text-brand-400"
                >
                  {product.url.replace(/^https?:\/\//, '')}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">Type</dt>
            <dd className="mt-0.5 capitalize">{product.type}</dd>
          </div>
          {product.launched_at && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">Launched</dt>
              <dd className="mt-0.5">{formatDate(product.launched_at)}</dd>
            </div>
          )}
          {product.stack && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">Built with</dt>
              <dd className="mt-0.5">{product.stack}</dd>
            </div>
          )}
        </dl>
        {product.description && (
          <p className="mt-4 border-t border-paper-200 pt-4 text-sm text-ink-600 dark:border-ink-700 dark:text-paper-300">
            {product.description}
          </p>
        )}
      </section>

      <section className="card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
            Performance
          </h2>
          {available.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="field w-auto py-1.5"
                aria-label="Metric"
                value={metric ?? ''}
                onChange={(e) => setMetric(e.target.value)}
              >
                {available.map((m) => (
                  <option key={m.metric} value={m.metric}>{metricLabel(m.metric)}</option>
                ))}
              </select>
              <div className="flex rounded-lg border border-paper-300 dark:border-ink-700" role="group" aria-label="Period">
                {RANGES.map((r) => (
                  <button
                    key={r.days}
                    type="button"
                    onClick={() => setRangeDays(r.days)}
                    className={`px-3 py-1.5 text-xs font-medium first:rounded-l-lg last:rounded-r-lg ${
                      rangeDays === r.days
                        ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                        : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {available.length === 0 ? (
          <Empty
            title="No performance data yet"
            description="Metrics appear here once this product starts reporting."
          />
        ) : points === null ? (
          <Loading />
        ) : (
          <>
            <SeriesChart points={points} />
            {sources.length > 0 && (
              <p className="mt-2 text-right text-[11px] text-ink-400">
                Source: {sources.join(', ')}
              </p>
            )}
          </>
        )}
      </section>

      {activeModules.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-semibold">Manage</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {activeModules.map((key) => (
              <Link
                key={key}
                to={`/products/${id}/manage/${key}`}
                className="card block p-5 transition-shadow hover:shadow-md"
              >
                <div className="flex items-center gap-3">
                  <span className="rounded-lg bg-brand-500 p-2 text-white">
                    <Wrench className="h-4 w-4" aria-hidden />
                  </span>
                  <div>
                    <h3 className="font-semibold">{moduleLabel(key)}</h3>
                    <p className="text-xs text-ink-500 dark:text-ink-400">
                      Manage this product's backend
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {user?.role === 'owner' && id && <ReadinessPanel productId={id} />}

      {user?.role === 'owner' && dataSources && <DataSources sources={dataSources} />}

      {audience && Object.keys(audience.dimensions).length > 0 && (
        <section>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">Audience</h2>
            {audience.sources.length > 0 && (
              <span className="text-[11px] text-ink-400">
                Source: {audience.sources.join(', ')}
              </span>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Object.entries(AUDIENCE_LABELS)
              .filter(([dimension]) => audience.dimensions[dimension]?.length)
              .map(([dimension, label]) => (
                <AudienceCard
                  key={dimension}
                  title={label}
                  dimension={dimension}
                  rows={audience.dimensions[dimension]}
                />
              ))}
          </div>
        </section>
      )}

      {audience && audience.flow?.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-semibold">Where visitors go next</h2>
          <div className="card p-4">
            <FlowChart edges={audience.flow} />
            <p className="mt-3 text-[11px] text-ink-400">
              Each ribbon is one page-to-page move, thickness by volume.
              <strong> (entry)</strong> is the first page of a visit. A journey is
              followed within a single browser tab and is not stored anywhere, so
              it ends when the tab does.
            </p>
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-3 text-lg font-semibold">Documents for this product</h2>
        {documents.length === 0 ? (
          <div className="card"><Empty title="Nothing published for this product yet" /></div>
        ) : (
          <div className="card divide-y divide-paper-200 dark:divide-ink-700">
            {documents.map((d) => (
              <Link
                key={d.id}
                to={`/documents/${d.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-paper-50 dark:hover:bg-ink-700/50"
              >
                <FileText className="h-4 w-4 shrink-0 text-ink-400" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{d.title}</p>
                  <p className="text-xs text-ink-500 dark:text-ink-400">
                    {CATEGORY_LABELS[d.category]} · {formatDate(d.updated_at)}
                  </p>
                </div>
                {d.pinned === 1 && <Pin className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden />}
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
